import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  LoadingState,
  Toast,
} from '@biznomi/ui-react'
import { getEmployees, getNfcSpots, type EmployeeRow, type NfcSpot } from '@/lib/api/admin'
import {
  getNfcMappings,
  setEmployeeSpots,
  type MappedEmployee,
} from '@/lib/api/nfcMapping'

/**
 * 담당 근무지 — **대리출근 차단의 핵심 화면** (ADMIN 전용).
 *
 * 규칙이 직관과 반대라 화면이 그걸 계속 말해 줘야 한다:
 *   매핑 **없음** → 모든 태그판에서 찍힌다 (제한 없음)
 *   매핑 **있음** → 그 자리에서만 찍힌다
 *
 * 그래서 "지우면 잠긴다"가 아니라 **"지우면 풀린다"**. 관리자가 반대로 이해하면
 * 제한을 걸었다고 믿는 채로 아무 데서나 찍히게 된다 — 우리가 파는 것이 무너진다.
 *
 * 서버는 `nfcCheckin` 에서 이 목록을 whitelist 로 검증하고, 벗어나면
 * `SPOT_NOT_ASSIGNED` 로 거절한다.
 */

export default function NfcMappings() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<{ employee: EmployeeRow; current: string[] } | null>(null)
  const [clearTarget, setClearTarget] = useState<MappedEmployee | null>(null)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')

  const { data: mapData, isLoading } = useQuery({
    queryKey: ['nfc-mappings'],
    queryFn: getNfcMappings,
  })
  const { data: empData } = useQuery({
    queryKey: ['employees', 'for-mapping'],
    queryFn: () => getEmployees({ take: 1000, status: 'active' }),
  })
  const { data: spotData } = useQuery({ queryKey: ['nfc-spots'], queryFn: getNfcSpots })

  const mapped = mapData?.success ? mapData.data ?? [] : []
  const employees = useMemo(
    () => (empData?.success ? empData.data?.employees ?? [] : []),
    [empData],
  )
  const spots = useMemo(() => (spotData?.success ? spotData.data ?? [] : []), [spotData])

  /** 매핑이 있는 직원 id — 나머지는 "제한 없음" */
  const restrictedIds = useMemo(() => new Set(mapped.map((m) => m.employeeId)), [mapped])
  const unrestricted = useMemo(
    () => employees.filter((e) => !restrictedIds.has(e.id)),
    [employees, restrictedIds],
  )

  const q = search.trim().toLowerCase()
  const match = (name: string, num: string | null) =>
    !q || name.toLowerCase().includes(q) || (num ?? '').toLowerCase().includes(q)

  const shownMapped = mapped.filter((m) => match(m.employeeName, m.employeeNumber))
  const shownFree = unrestricted.filter((e) => match(e.name, e.employeeNumber))

  async function clearRestriction() {
    if (!clearTarget) return
    const res = await setEmployeeSpots(clearTarget.employeeId, [])
    setClearTarget(null)
    if (!res.success) {
      setError(res.error ?? '해제하지 못했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['nfc-mappings'] })
    setToast(`${clearTarget.employeeName} 님은 이제 어디서나 찍을 수 있습니다`)
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">담당 근무지</h1>

      {/*
        이 안내가 이 화면에서 제일 중요하다. 규칙이 직관과 반대라
        한 번 잘못 이해하면 제한을 걸었다고 믿는 채로 아무 데서나 찍힌다.
      */}
      <InfoBox tone="info">
        직원마다 어디서 찍을 수 있는지 정해 둡니다. 근무지를 정해 주면 <b>그 자리에서만</b> 찍힙니다.
        아무 데도 안 정하면 <b>어디서나</b> 찍힙니다. 남이 대신 찍어 주는 걸 막으려면 정해 두세요.
      </InfoBox>

      <div className="grid grid-cols-2 gap-2 rounded-card bg-white p-4">
        <div className="text-center">
          <p className="text-xs text-slate-500">근무지 지정됨</p>
          <p className="text-xl font-bold text-primary">
            {mapped.length}<span className="text-sm font-normal text-slate-400">명</span>
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-slate-500">제한 없음</p>
          <p className="text-xl font-bold">
            {unrestricted.length}<span className="text-sm font-normal text-slate-400">명</span>
          </p>
        </div>
      </div>

      <Input
        placeholder="이름 · 사번 검색"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : (
        <>
          <section className="space-y-2">
            <h2 className="text-sm font-bold text-slate-500">근무지 지정됨 ({shownMapped.length})</h2>
            {shownMapped.length === 0 ? (
              <EmptyState
                title="지정된 직원이 없습니다"
                hint="아래 «제한 없음» 목록에서 직원을 골라 근무지를 지정하세요."
              />
            ) : (
              <ul className="divide-y divide-slate-100 rounded-card bg-white">
                {shownMapped.map((m) => (
                  <li key={m.employeeId} className="space-y-2 px-4 py-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {m.employeeName}
                          <span className="ml-1.5 text-xs font-normal text-slate-400">
                            {[m.employeeNumber, m.departmentName].filter(Boolean).join(' · ')}
                          </span>
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {m.spots.map((s) => (
                            <Badge key={s.id} variant="purple">
                              {s.location ? `${s.location} ${s.name}` : s.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const emp = employees.find((e) => e.id === m.employeeId)
                          if (emp) setEditing({ employee: emp, current: m.spots.map((s) => s.id) })
                        }}
                      >
                        변경
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setClearTarget(m)}>
                        제한 해제
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-bold text-slate-500">
              제한 없음 ({shownFree.length}) · 어디서나 찍힙니다
            </h2>
            {shownFree.length === 0 ? (
              <EmptyState title="전원 근무지가 지정되어 있습니다" />
            ) : (
              <ul className="divide-y divide-slate-100 rounded-card bg-white">
                {shownFree.slice(0, 50).map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{e.name}</p>
                      <p className="truncate text-xs text-slate-400">
                        {[e.employeeNumber, e.department?.name].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="shrink-0"
                      onClick={() => setEditing({ employee: e, current: [] })}
                    >
                      근무지 지정
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {shownFree.length > 50 && (
              <p className="text-xs text-slate-400">
                {shownFree.length}명 중 50명만 보입니다. 검색으로 좁혀 주세요.
              </p>
            )}
          </section>
        </>
      )}

      {editing && (
        <SpotPickerDialog
          employee={editing.employee}
          current={editing.current}
          spots={spots}
          onClose={() => setEditing(null)}
          onDone={(msg) => {
            setEditing(null)
            qc.invalidateQueries({ queryKey: ['nfc-mappings'] })
            setToast(msg)
          }}
        />
      )}

      {clearTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setClearTarget(null)}
          title="제한을 풀까요?"
          message={`${clearTarget.employeeName} 님이 앞으로 어느 근무지에서든 찍을 수 있게 됩니다. 막는 게 아니라 푸는 겁니다.`}
          confirmText="제한 해제"
          destructive
          onConfirm={() => void clearRestriction()}
        />
      )}

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}

/** 근무지 여러 곳을 골라 통째로 교체한다 (서버 /set 이 교체 방식) */
function SpotPickerDialog({
  employee,
  current,
  spots,
  onClose,
  onDone,
}: {
  employee: EmployeeRow
  current: string[]
  spots: NfcSpot[]
  onClose: () => void
  onDone: (message: string) => void
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set(current))
  const [q, setQ] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const active = spots.filter((s) => s.isActive)
  const query = q.trim().toLowerCase()
  const shown = active.filter(
    (s) =>
      !query ||
      s.name.toLowerCase().includes(query) ||
      (s.location ?? '').toLowerCase().includes(query),
  )

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function save() {
    setSaving(true)
    setError('')
    const ids = [...picked]
    const res = await setEmployeeSpots(employee.id, ids)
    setSaving(false)
    if (!res.success) {
      setError(res.error ?? '저장하지 못했습니다')
      return
    }
    onDone(
      ids.length === 0
        ? `${employee.name} 님은 이제 어디서나 찍을 수 있습니다`
        : `${employee.name} 근무지 ${ids.length}곳 지정`,
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogTitle>{employee.name} 님 근무지</DialogTitle>

        <div className="space-y-3">
          <Input placeholder="근무지 이름 · 코드 검색" value={q} onChange={(e) => setQ(e.target.value)} />

          {active.length === 0 ? (
            <EmptyState title="등록된 근무지가 없습니다" hint="«근무지» 메뉴에서 먼저 등록하세요." />
          ) : (
            <ul className="max-h-72 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-2">
              {shown.map((s) => {
                const on = picked.has(s.id)
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => toggle(s.id)}
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm ${
                        on ? 'bg-primary/10 font-semibold text-primary' : 'bg-white text-slate-600'
                      }`}
                    >
                      <span className="w-4 shrink-0">{on ? '✓' : ''}</span>
                      <span className="min-w-0 flex-1 truncate">
                        {s.name}
                        {s.location && <span className="ml-1.5 text-xs text-slate-400">{s.location}</span>}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {/* 0곳 선택은 "제한 해제"라는 걸 저장 전에 말해 준다 */}
          {picked.size === 0 ? (
            <p className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-xs text-[var(--color-danger)]">
              한 곳도 안 고르면 <b>제한이 풀립니다.</b> 이 직원은 어디서나 찍게 됩니다.
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              {picked.size}곳 골랐습니다. 이 직원은 <b>여기서만</b> 찍을 수 있습니다.
            </p>
          )}

          {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}

          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={onClose} disabled={saving}>
              취소
            </Button>
            <Button className="flex-1" loading={saving} onClick={() => void save()}>
              저장
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
